import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Collapse,
  Space,
  Typography,
  Card,
  Empty,
  Modal,
  Button,
  message,
  Spin,
  Checkbox,
  FloatButton,
  Tag,
  Divider,
  Input,
  List,
  Avatar,
} from "antd";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  IdcardOutlined,
  HomeOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import axios from "axios";
import {
  GET_GEO_ALL,
  HEADERS_EXPORT_BEN,
  POST_ACCEPT_LAWYER,
  POST_LAWSUIT_DETAIL,
} from "../../../API/apiUrls";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import DebtPayment from "../../preInvestigateAssets/modal/DebtPayment";

const CheckingDocuments = ({
  open,
  close,
  dataDefault,
  funcUpdateStatus,
  status = dataDefault.currentStatus,
}) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const [loading, setLoading] = useState(false);
  const [checkedList, setCheckedList] = useState({});
  const [verifyList, setVerifyList] = useState({}); // ✅ เพิ่ม state สำหรับ checkbox ฝั่งขวา
  const scrollRef = useRef(null);
  const [dataLoadLawSuit, setDataLoadLawSuit] = useState(null);
  const [dataAsset, setDataAsset] = useState([]);
  const [dataWCC, setDataWCC] = useState([]);
  const [activeKey, setActiveKey] = useState([]);
  const [geoRawData, setGeoRawData] = useState(null);
  const [memoData, setMemoData] = useState(null);
  const [isModalDebtPayment, setIsModalDebtPayment] = useState(false);
  const [newMemoText, setNewMemoText] = useState(""); // หมายเหตุใหม่ที่กำลังพิมพ์

  // ✅ โหลด GEO Data (ครั้งเดียว)
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadGeoData = async () => {
      try {
        const res = await axios.get(GET_GEO_ALL, {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        });

        if (isMounted) {
          setGeoRawData(res.data);
          console.log("✅ geoRawData loaded");
        }
      } catch (error) {
        if (error.name !== "CanceledError" && isMounted) {
          console.error("❌ Error loading geo data:", error);
        }
      }
    };

    loadGeoData();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // ✅ โหลดข้อมูล Lawsuit
  const loadLawsuitData = useCallback(async () => {
    if (!dataDefault) return null;

    setLoading(true);
    const controller = new AbortController();

    try {
      console.log("📋 Loading lawsuit data for:", dataDefault);

      const lawsuitRes = await axios.post(
        POST_LAWSUIT_DETAIL,
        {
          CONTNO: dataDefault.CONTNO,
          loanId: dataDefault.loanId,
          lawsuitId: dataDefault.lawsuitId || 0,
        },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        },
      );

      if (lawsuitRes?.data) {
        console.log("✅ Lawsuit data loaded:", lawsuitRes.data);
        oldDataLawsuitDoc(lawsuitRes.data);
        setDataLoadLawSuit(lawsuitRes.data.lawsuit);
        setDataAsset(lawsuitRes.data.asset || []);
        setDataWCC(lawsuitRes.data.wcc || []);
        setMemoData(lawsuitRes.data.note || []);
        return lawsuitRes.data.lawsuit;
      } else {
        throw new Error("ไม่พบข้อมูลคดี");
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("❌ Error loading lawsuit:", error);
        const errorMsg = error.response?.data?.message || error.message;
        message.error(`เกิดข้อผิดพลาด: ${errorMsg}`);
        setDataLoadLawSuit(null);
        setDataAsset([]);
        setDataWCC([]);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [dataDefault]);

  // ✅ โหลดข้อมูลเมื่อเปิด Modal
  useEffect(() => {
    if (!open) return;

    // Reset checked list และ verify list เมื่อเปิด modal
    setCheckedList({});
    setVerifyList({});
    loadLawsuitData();
  }, [open, loadLawsuitData]);

  const oldDataLawsuitDoc = (rawData) => {
    if ((status === 3 || status === 6) && rawData?.lawsuit?.workLawsuitDoc) {
      const doc = rawData.lawsuit.workLawsuitDoc;
      const loanOld = rawData.lawsuit.loanOld; // ✅ เพิ่มตัวแปรนี้

      console.log("📝 โหลดข้อมูลเดิมจาก workLawsuitDoc:", doc);
      console.log("📝 ข้อมูล loanOld:", loanOld);

      // สร้าง initial checked list
      const initialCheckedList = {
        contract: doc.docContno || false,
        property_docs: doc.docMainAsset || false,
        debtor_card: doc.docDebtorCard || false,
        id_cards: doc.docCopyCard || false,
        tr14: doc.docAddressTr || false,
        notice: doc.docNotify || false,
        termination: doc.docWcc || false,
      };

      // ✅ โหลด verify list (ถ้ามี field ในฐานข้อมูล)
      const initialVerifyList = {
        contract_verify: doc.docContnoLawyer || false,
        property_docs_verify: doc.docMainAssetLawyer || false,
        debtor_card_verify: doc.docDebtorCardLawyer || false,
        notice_verify: doc.docNotifyLawyer || false,
        termination_verify: doc.docWccLawyer || false,
        id_cards_verify: doc.docCopyCardLawyer || false,
        tr14_verify: doc.docAddressTrLawyer || false,
      };

      if (doc.docCopyCard && rawData.asset) {
        // ✅ Tick ทุกคนโดยไม่ต้องแยกกลุ่ม
        rawData.asset.forEach((person) => {
          if (person.customerId) {
            initialCheckedList[`id_card_${person.customerId}`] = true;
          }
        });
      }

      // ✅ 2. ถ้า docAddressTr = true ให้ tick ที่อยู่ ทร.14 ทุกคน
      if (doc.docAddressTr && loanOld) {
        // Tick ที่อยู่ ทร.14 ของผู้เช่าซื้อ
        if (loanOld.addressTr && loanOld.addressTr.length > 0) {
          const address = loanOld.addressTr[0];
          initialCheckedList[
            `tr14_${loanOld.CUSTOMER_ID}_${address.addrTrId}`
          ] = true;
        }

        // Tick ที่อยู่ ทร.14 ของผู้ค้ำทุกคน
        if (loanOld.guarantors && loanOld.guarantors.length > 0) {
          loanOld.guarantors.forEach((guarantor) => {
            if (guarantor.addressTr && guarantor.addressTr.length > 0) {
              const address = guarantor.addressTr[0];
              initialCheckedList[
                `tr14_${guarantor.CUSTOMER_ID}_${address.addrTrId}`
              ] = true;
            }
          });
        }
      }

      // 3. ถ้า docWcc = true ให้ tick บอกเลิกทุกรายการ
      if (doc.docWcc && rawData.wcc) {
        rawData.wcc.forEach((item) => {
          initialCheckedList[`termination_${item.id}`] = true;
        });
      }

      // 4. ถ้า docNotify มีค่า ให้ tick บอกกล่าวทุกรายการ (ถ้ามี)
      if (doc.docNotify) {
        rawData.wcc.forEach((item) => {
          initialCheckedList[`notice_${item.id}`] = true;
        });
      }

      setCheckedList(initialCheckedList);
      setVerifyList(initialVerifyList);

      console.log("✅ โหลดข้อมูลเดิมเสร็จสิ้น:", initialCheckedList);
    }
  };

  const handleCancel = () => {
    close();
  };

  const handleSave = async (status) => {
    setLoading(true);
    try {
      // ✅ สร้าง payload สำหรับ API
      const payload = {
        docId: dataLoadLawSuit.workLawsuitDoc.docId,
        lawsuitId: dataDefault.lawsuitId,
        loanId: dataDefault.loanId,
        currentStatus: status,
        userId: userId,
        docContnoLawyer: verifyList["contract_verify"] || false,
        docMainAssetLawyer: verifyList["property_docs_verify"] || false,
        docDebtorCardLawyer: verifyList["debtor_card_verify"] || false,
        docNotifyLawyer: verifyList["notice_verify"] || false,
        docWccLawyer: verifyList["termination_verify"] || false,
        docCopyCardLawyer: verifyList["id_cards_verify"] || false,
        docAddressTrLawyer: verifyList["tr14_verify"] || false,
        docMark: newMemoText,
      };

      console.log("📌 Payload ส่ง API:", payload);

      const result = await postDocStatus(payload);

      if (result) {
        funcUpdateStatus({
          action: "refresh",
          data: result,
        });
      }

      message.success("บันทึกสำเร็จ");
      close();
    } catch (err) {
      console.error("❌ Error saving:", err);
      message.error(status === 3 ? "แก้ไขไม่สำเร็จ" : "บันทึกไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  // ✅ ฟังก์ชันส่งข้อมูลไป API
  const postDocStatus = async (payload) => {
    console.log("📤 POST_DOC_STATUS with data:", payload);

    try {
      const res = await axios.post(POST_ACCEPT_LAWYER, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      const { status, data } = res;

      if (status === 200) {
        console.log("✅ API Response:", data);
        return data;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      }

      console.error("❌ postDocStatus error:", error);
      throw error;
    }
  };

  const handleCheckboxChange = useCallback((id, checked) => {
    setCheckedList((prev) => ({
      ...prev,
      [id]: checked,
    }));
  }, []);

  // ✅ Handler สำหรับ verify checkbox
  const handleVerifyChange = useCallback((id, checked) => {
    setVerifyList((prev) => ({
      ...prev,
      [id]: checked,
    }));
  }, []);

  // ✅ Handler: Callback จาก AddAddressTr
  const handleLoad = useCallback(
    async (data) => {
      console.log("📥 Callback from AddAddressTr:", data);

      if (data?.action === "refresh") {
        // ⭐ โหลดเฉพาะ lawsuit ใหม่
        await loadLawsuitData();
        message.success("อัพเดทข้อมูลที่อยู่ ทร.14 สำเร็จ");
      }
    },
    [loadLawsuitData],
  );

  // ✅ ฟังก์ชันแปลง code เป็นชื่อ GEO
  const getGeoNameFromCode = useCallback(
    (provCode, distCode, subDistCode) => {
      let provName = null;
      let distName = null;
      let subDistName = null;

      if (geoRawData) {
        const province = geoRawData.province?.find(
          (p) => String(p.provCode) === String(provCode),
        );
        provName = province?.provDesc || null;

        if (province && distCode) {
          const district = geoRawData.district?.find(
            (d) =>
              String(d.provCode) === String(provCode) &&
              String(d.distCode) === String(distCode),
          );
          distName = district?.distDesc || null;
        }

        if (province && distCode && subDistCode) {
          const subDistrict = geoRawData.subDistrict?.find(
            (s) =>
              String(s.provCode) === String(provCode) &&
              String(s.distCode) === String(distCode) &&
              String(s.subDistCode) === String(subDistCode),
          );
          subDistName = subDistrict?.subDistDesc || null;
        }
      }

      return { provName, distName, subDistName };
    },
    [geoRawData],
  );

  // ✅ ฟังก์ชันสร้างที่อยู่เต็ม
  const formatFullAddress = useCallback(
    (address) => {
      const parts = [
        address.houseNumber,
        address.soi,
        address.road,
        address.village,
      ].filter(Boolean);

      const geoNames = getGeoNameFromCode(
        address.province,
        address.district,
        address.subdistrict,
      );

      const geoParts = [
        geoNames.subDistName,
        geoNames.distName,
        geoNames.provName,
        address.zipcode,
      ].filter(Boolean);

      return {
        address: parts.join(" "),
        geo: geoParts.join(" "),
        fullAddress: [...parts, ...geoParts].join(" "),
        province: geoNames.provName,
        district: geoNames.distName,
        subdistrict: geoNames.subDistName,
      };
    },
    [getGeoNameFromCode],
  );

  // ✅ ตรวจสอบว่าเป็นที่อยู่ที่ไม่เกิน 1 เดือน
  const isWithinOneMonth = useCallback((createdAt) => {
    if (!createdAt) return false;
    const oneMonthAgo = dayjs().subtract(1, "month");
    return dayjs(createdAt).isAfter(oneMonthAgo);
  }, []);

  // ✅ สร้างรายการ ทร.14 พร้อมกรองตามวันที่ (เฉพาะที่อยู่ล่าสุด 1 ที่อยู่)
  const getTr14List = useCallback(() => {
    if (!dataLoadLawSuit?.loanOld) return [];

    const list = [];
    const loanOld = dataLoadLawSuit.loanOld;

    // ผู้เช่าซื้อ
    if (loanOld.addressTr && loanOld.addressTr.length > 0) {
      const borrower = dataAsset.find((item) => item.garno === 0);
      const borrowerMoney = borrower?.money;

      // ⭐ กรองเฉพาะที่อยู่ที่ไม่เกิน 1 เดือน
      const validAddresses = loanOld.addressTr.filter((addr) =>
        isWithinOneMonth(addr.CreatedAt),
      );

      // ⭐ เลือกเฉพาะที่อยู่ล่าสุด (1 ที่อยู่)
      if (validAddresses.length > 0) {
        const latestAddress = validAddresses.reduce((latest, current) => {
          return dayjs(current.CreatedAt).isAfter(dayjs(latest.CreatedAt))
            ? current
            : latest;
        });

        list.push({
          customerId: loanOld.CUSTOMER_ID,
          type: "borrower",
          garno: 0,
          name: borrower
            ? `${borrower.SNAM}${borrower.NAME1} ${borrower.NAME2}`
            : "ผู้เช่าซื้อ",
          addressTr: [
            {
              ...latestAddress,
              ...formatFullAddress(latestAddress),
            },
          ],
          money: borrowerMoney,
        });
      }
    }

    // ผู้ค้ำประกัน
    if (loanOld.guarantors && loanOld.guarantors.length > 0) {
      loanOld.guarantors.forEach((guarantor) => {
        if (guarantor.addressTr && guarantor.addressTr.length > 0) {
          const guarantorAsset = dataAsset.find(
            (item) => item.garno === guarantor.garno,
          );
          const guarantorMoney = guarantorAsset?.money;

          // ⭐ กรองเฉพาะที่อยู่ที่ไม่เกิน 1 เดือน
          const validAddresses = guarantor.addressTr.filter((addr) =>
            isWithinOneMonth(addr.CreatedAt),
          );

          // ⭐ เลือกเฉพาะที่อยู่ล่าสุด (1 ที่อยู่)
          if (validAddresses.length > 0) {
            const latestAddress = validAddresses.reduce((latest, current) => {
              return dayjs(current.CreatedAt).isAfter(dayjs(latest.CreatedAt))
                ? current
                : latest;
            });

            list.push({
              customerId: guarantor.CUSTOMER_ID,
              type: "guarantor",
              garno: guarantor.garno,
              name: guarantorAsset
                ? `${guarantorAsset.SNAM}${guarantorAsset.NAME1} ${guarantorAsset.NAME2}`
                : `ผู้ค้ำคนที่ ${guarantor.garno}`,
              relation: guarantor.RELATN,
              addressTr: [
                {
                  ...latestAddress,
                  ...formatFullAddress(latestAddress),
                },
              ],
              money: guarantorMoney,
            });
          }
        }
      });
    }

    return list;
  }, [dataLoadLawSuit, dataAsset, isWithinOneMonth, formatFullAddress]);

  // ⭐ ใช้ useMemo แทน useEffect เพื่อคำนวณสถานะ parent checkbox

  // 1. สำเนาบัตรประชาชน - เช็คจากจำนวน customer + guarantor
  const idCardsChecked = useMemo(() => {
    // ✅ ผู้เช่าซื้อหลัก
    const mainBorrower = dataAsset.find(
      (item) => item.garno === 0 && !item.relationCustomerId,
    );

    // ✅ คนที่เกี่ยวข้องทั้งหมด (คู่สมรส/ญาติ)
    const relations = dataAsset.filter((item) => item.relationCustomerId);

    // ✅ ผู้ค้ำทั้งหมด
    const guarantors = dataAsset.filter(
      (item) => item.garno > 0 && !item.relationCustomerId,
    );

    // ✅ รวมทุกคน
    const allPeople = [
      mainBorrower?.customerId,
      ...relations.map((r) => r.customerId),
      ...guarantors.map((g) => g.customerId),
    ].filter(Boolean);

    if (allPeople.length === 0) return false;

    const allChecked = allPeople.every((id) => checkedList[`id_card_${id}`]);

    console.log("✅ คำนวณ idCardsChecked:", {
      totalPeople: allPeople.length,
      mainBorrower: mainBorrower?.customerId,
      relations: relations.length,
      guarantors: guarantors.length,
      allChecked,
    });

    return allChecked;
  }, [dataAsset, checkedList]);

  // 2. บอกกล่าว - เช็คจากจำนวน WCC ที่ flagStatus === 1
  const noticeChecked = useMemo(() => {
    const wccNotice = dataWCC.filter((item) => item.flagStatus === 1);
    const allNoticeIds = wccNotice.map((item) => item.id);

    if (allNoticeIds.length === 0) return false;

    const allChecked = allNoticeIds.every((id) => checkedList[`notice_${id}`]);

    console.log("✅ คำนวณ noticeChecked:", {
      totalNotices: allNoticeIds.length,
      allChecked,
    });

    return allChecked;
  }, [dataWCC, checkedList]);

  // 3. บอกเลิก - เช็คจากจำนวน WCC ทั้งหมด
  const terminationChecked = useMemo(() => {
    const allTerminationIds = dataWCC.map((item) => item.id);

    if (allTerminationIds.length === 0) return false;

    const allChecked = allTerminationIds.every(
      (id) => checkedList[`termination_${id}`],
    );

    console.log("✅ คำนวณ terminationChecked:", {
      totalTerminations: allTerminationIds.length,
      allChecked,
    });

    return allChecked;
  }, [dataWCC, checkedList]);

  // 4. ทร.14 - เช็คจากที่อยู่ล่าสุดของแต่ละ customer
  const tr14Checked = useMemo(() => {
    const tr14List = getTr14List();

    // รวบรวม ID ของที่อยู่ทั้งหมด
    const allAddressIds = [];
    tr14List.forEach((person) => {
      person.addressTr.forEach((addr) => {
        allAddressIds.push(`tr14_${person.customerId}_${addr.addrTrId}`);
      });
    });

    if (allAddressIds.length === 0) return false;

    const allChecked = allAddressIds.every((id) => checkedList[id]);

    console.log("✅ คำนวณ tr14Checked:", {
      totalAddresses: allAddressIds.length,
      allChecked,
    });

    return allChecked;
  }, [getTr14List, checkedList]);

  // ✅ สร้าง items สำหรับ Collapse
  const getCollapseItems = () => {
    const items = [];

    // 1. สัญญา (ไม่สามารถกดเปิดได้)
    items.push({
      key: "contract",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              disabled
              checked={checkedList["contract"]}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                handleCheckboxChange("contract", e.target.checked)
              }
            />
            <FileTextOutlined />
            <Typography.Text>คำพิพากษา</Typography.Text>
          </Space>
          <Checkbox
            checked={verifyList["contract_verify"]}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              handleVerifyChange("contract_verify", e.target.checked)
            }
            style={{ marginRight: 16 }}
          >
            {verifyList["contract_verify"] ? (
              <CheckOutlined style={{ color: "#52c41a" }} />
            ) : (
              <CloseOutlined style={{ color: "#ff4d4f" }} />
            )}{" "}
            {verifyList["contract_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
          </Checkbox>
        </div>
      ),
      collapsible: "disabled",
      showArrow: false,
    });

    items.push({
      key: "property_docs",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              disabled
              checked={checkedList["property_docs"]}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                handleCheckboxChange("property_docs", e.target.checked)
              }
            />
            <HomeOutlined />
            <Typography.Text>
              <Typography.Text strong>หมายคดีตั้ง</Typography.Text>
            </Typography.Text>
          </Space>
          <Checkbox
            checked={verifyList["property_docs_verify"]}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              handleVerifyChange("property_docs_verify", e.target.checked)
            }
            style={{ marginRight: 16 }}
          >
            {verifyList["property_docs_verify"] ? (
              <CheckOutlined style={{ color: "#52c41a" }} />
            ) : (
              <CloseOutlined style={{ color: "#ff4d4f" }} />
            )}{" "}
            {verifyList["property_docs_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
          </Checkbox>
        </div>
      ),
      collapsible: "disabled",
      showArrow: false,
    });

    // 6. สำเนาบัตร ทุกคน (มี nested checkbox)
    const mainBorrower = dataAsset.find(
      (item) => item.garno === 0 && !item.relationCustomerId,
    );

    const borrowerRelations = dataAsset.filter(
      (item) => item.relationCustomerId === mainBorrower?.customerId,
    );

    const mainGuarantors = dataAsset.filter(
      (item) => item.garno > 0 && !item.relationCustomerId,
    );

    items.push({
      key: "id_cards",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              checked={idCardsChecked}
              onClick={(e) => e.stopPropagation()}
              disabled
            />
            <IdcardOutlined />
            <Typography.Text>
              คัด ทร.14{" "}
              <Tag color="cyan" style={{ marginLeft: 8 }}>
                {dataAsset.length} คน
              </Tag>
            </Typography.Text>
          </Space>

          <Checkbox
            checked={verifyList["id_cards_verify"]}
            onClick={(e) => e.stopPropagation()}
            style={{ marginRight: 16 }}
          >
            {verifyList["id_cards_verify"] ? (
              <CheckOutlined style={{ color: "#52c41a" }} />
            ) : (
              <CloseOutlined style={{ color: "#ff4d4f" }} />
            )}{" "}
            {verifyList["id_cards_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
          </Checkbox>
        </div>
      ),
      children: (
        <Card size="small">
          {/* ✅ 1. ผู้เช่าซื้อและคนที่เกี่ยวข้อง */}
          <div style={{ marginBottom: 12 }}>
            <Typography.Text>1. ผู้เช่าซื้อ</Typography.Text>
            {mainBorrower ? (
              <>
                {/* ผู้เช่าซื้อหลัก */}
                <div style={{ marginLeft: 16, marginTop: 4 }}>
                  <Space>
                    <Checkbox
                      checked={
                        checkedList[`id_card_${mainBorrower.customerId}`]
                      }
                      disabled
                      onChange={(e) =>
                        handleCheckboxChange(
                          `id_card_${mainBorrower.customerId}`,
                          e.target.checked,
                        )
                      }
                    />
                    <Typography.Text>
                      {mainBorrower.SNAM}
                      {mainBorrower.NAME1} {mainBorrower.NAME2}
                    </Typography.Text>
                  </Space>
                  <div style={{ marginLeft: 40 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                      เลขบัตรประชาชน: {mainBorrower.CUSCOD}
                    </Typography.Text>
                  </div>
                </div>

                {/* ✅ คนที่เกี่ยวข้องกับผู้เช่าซื้อ (คู่สมรส/ญาติ) */}
                {borrowerRelations.length > 0 &&
                  borrowerRelations.map((relation) => (
                    <div
                      key={relation.customerId}
                      style={{ marginLeft: 40, marginTop: 8 }}
                    >
                      <Space>
                        <Checkbox
                          checked={
                            checkedList[`id_card_${relation.customerId}`]
                          }
                          disabled
                          onChange={(e) =>
                            handleCheckboxChange(
                              `id_card_${relation.customerId}`,
                              e.target.checked,
                            )
                          }
                        />
                        <Typography.Text>
                          {relation.SNAM}
                          {relation.NAME1} {relation.NAME2}
                          <Tag color="green" style={{ marginLeft: 8 }}>
                            {relation.relation}
                          </Tag>
                        </Typography.Text>
                      </Space>
                      <div style={{ marginLeft: 40 }}>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 14 }}
                        >
                          เลขบัตรประชาชน: {relation.CUSCOD}
                        </Typography.Text>
                      </div>
                    </div>
                  ))}
              </>
            ) : (
              <Typography.Text type="secondary">ไม่พบข้อมูล</Typography.Text>
            )}
          </div>

          <Divider style={{ margin: "12px 0" }} />

          {/* ✅ 2. ผู้ค้ำและคนที่เกี่ยวข้อง */}
          <div>
            <Typography.Text>
              2. คนค้ำประกัน ({mainGuarantors.length} คน)
            </Typography.Text>
            {mainGuarantors.length > 0 ? (
              mainGuarantors.map((guarantor, index) => {
                // ✅ หาคนที่เกี่ยวข้องกับผู้ค้ำคนนี้
                const guarantorRelations = dataAsset.filter(
                  (item) => item.relationCustomerId === guarantor.customerId,
                );

                return (
                  <div key={guarantor.customerId} style={{ marginTop: 8 }}>
                    {/* ผู้ค้ำหลัก */}
                    <div style={{ marginLeft: 16 }}>
                      <Space>
                        <Checkbox
                          disabled
                          checked={
                            checkedList[`id_card_${guarantor.customerId}`]
                          }
                          onChange={(e) =>
                            handleCheckboxChange(
                              `id_card_${guarantor.customerId}`,
                              e.target.checked,
                            )
                          }
                        />
                        <Typography.Text>
                          {index + 1}. {guarantor.SNAM}
                          {guarantor.NAME1} {guarantor.NAME2}
                        </Typography.Text>
                      </Space>
                      <div style={{ marginLeft: 40 }}>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 14 }}
                        >
                          เลขบัตรประชาชน: {guarantor.CUSCOD}
                        </Typography.Text>
                      </div>
                    </div>

                    {/* ✅ คนที่เกี่ยวข้องกับผู้ค้ำ (ถ้ามี) */}
                    {guarantorRelations.length > 0 &&
                      guarantorRelations.map((relation) => (
                        <div
                          key={relation.customerId}
                          style={{ marginLeft: 40, marginTop: 8 }}
                        >
                          <Space>
                            <Checkbox
                              disabled
                              checked={
                                checkedList[`id_card_${relation.customerId}`]
                              }
                              onChange={(e) =>
                                handleCheckboxChange(
                                  `id_card_${relation.customerId}`,
                                  e.target.checked,
                                )
                              }
                            />
                            <Typography.Text>
                              {relation.SNAM}
                              {relation.NAME1} {relation.NAME2}
                              <Tag color="green" style={{ marginLeft: 8 }}>
                                {relation.relation}
                              </Tag>
                            </Typography.Text>
                          </Space>
                          <div style={{ marginLeft: 40 }}>
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 14 }}
                            >
                              เลขบัตรประชาชน: {relation.CUSCOD}
                            </Typography.Text>
                          </div>
                        </div>
                      ))}

                    {index < mainGuarantors.length - 1 && (
                      <Divider style={{ margin: "12px 0" }} />
                    )}
                  </div>
                );
              })
            ) : (
              <Typography.Text type="secondary">ไม่มีคนค้ำ</Typography.Text>
            )}
          </div>
        </Card>
      ),
    });

    return items;
  };

  // ✅ สร้างข้อความแสดงชื่อผู้เช่าซื้อและผู้ค้ำ
  const getHeaderInfo = () => {
    const borrower = dataAsset.find((item) => item.garno === 0);
    const guarantors = dataAsset.filter((item) => item.garno > 0);

    return (
      <div>
        <div>
          <Typography.Text>เลขที่สัญญา: </Typography.Text>
          <Tag color="blue">{dataDefault?.CONTNO}</Tag>
          {status === 3 ? (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              ตรวจเอกสาร
            </Tag>
          ) : status === 6 ? (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              แก้ไข
            </Tag>
          ) : null}
        </div>
        {borrower && (
          <div style={{ marginTop: 4 }}>
            <Typography.Text>ผู้เช่าซื้อ: </Typography.Text>
            <Typography.Text>
              {borrower.SNAM}
              {borrower.NAME1} {borrower.NAME2}
            </Typography.Text>
          </div>
        )}
        {guarantors.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <Typography.Text>ผู้ค้ำประกัน: </Typography.Text>
            {guarantors.map((guarantor, index) => (
              <span key={guarantor.customerId}>
                {guarantor.SNAM}
                {guarantor.NAME1} {guarantor.NAME2}
                {index < guarantors.length - 1 ? ", " : ""}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
          <span>{status === 3 ? "ทนายตรวจเอกสาร" : "ตรวจสอบเอกสาร"}</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      width={800}
      maskClosable={false}
      centered
      footer={[
        <Button key="back" onClick={handleCancel} danger>
          ปิด
        </Button>,
        <Button
          key="reject"
          color="orange"
          variant="outlined"
          loading={loading}
          // onClick={() => handleSave(6)}
        >
          ตีกลับ
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          // onClick={() => handleSave(7)}
        >
          รับงาน
        </Button>,
      ]}
    >
      <div
        ref={scrollRef}
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          paddingRight: 8,
        }}
      >
        <Spin spinning={loading}>
          {dataLoadLawSuit ? (
            <>
              <Card
                size="small"
                style={{ marginBottom: 16, backgroundColor: "#f0f5ff" }}
              >
                {getHeaderInfo()}
              </Card>
              <Collapse
                items={getCollapseItems()}
                activeKey={activeKey}
                onChange={setActiveKey}
                style={{ marginBottom: 16 }}
              />
              {/* แสดงรายการหมายเหตุที่มีอยู่แล้ว */}
              {memoData?.length > 0 && (
                <>
                  <Divider orientation="left">
                    <Space>
                      <Typography.Text>หมายเหตุก่อนหน้า</Typography.Text>
                      <Tag color="blue">{memoData.length} รายการ</Tag>
                    </Space>
                  </Divider>
                  <List
                    style={{ marginBottom: 16 }}
                    bordered
                    dataSource={memoData}
                    renderItem={(item, index) => (
                      <List.Item>
                        <List.Item.Meta
                          // avatar={
                          //   <Avatar style={{ backgroundColor: "#1890ff" }}>
                          //     {item.USERNAME?.charAt(0).toUpperCase()}
                          //   </Avatar>
                          // }
                          title={
                            <Space>
                              <Typography.Text>
                                {item.USERNAME} :
                              </Typography.Text>
                              <Typography.Text
                                type="secondary"
                                style={{ fontSize: 12 }}
                              >
                                {dayjs(item.CreatedAt).format(
                                  "DD/MM/YYYY HH:mm",
                                )}
                              </Typography.Text>
                            </Space>
                          }
                          description={item.note}
                        />
                      </List.Item>
                    )}
                  />
                </>
              )}

              <Divider orientation="left">
                <Typography.Text>เพิ่มหมายเหตุใหม่</Typography.Text>
              </Divider>
              <Input.TextArea
                value={newMemoText}
                onChange={(e) => setNewMemoText(e.target.value)}
                rows={3}
                placeholder="กรอกหมายเหตุใหม่..."
                maxLength={500}
                showCount
              />
            </>
          ) : (
            <Empty description="ไม่มีข้อมูล" />
          )}
        </Spin>

        <FloatButton.BackTop
          target={() => scrollRef.current}
          visibilityHeight={80}
          style={{ right: 16, bottom: 16 }}
        />
      </div>

      {isModalDebtPayment && (
        <DebtPayment
          open={isModalDebtPayment}
          close={() => setIsModalDebtPayment(false)}
          initialContractNo={dataDefault.CONTNO}
          initialDataType={null}
          dataDefault={dataDefault}
        />
      )}
    </Modal>
  );
};

export default CheckingDocuments;
